import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ReferralLead {
  id: string;
  user_id: string;
  org_name: string;
  contact_name: string | null;
  contact_email: string | null;
  title: string | null;
  phone: string | null;
  address: string | null;
  zip_code: string | null;
  source_type: string | null;
  specialty_match: string[];
  status: string;
  enriched: boolean;
  enrichment_data: any;
  created_at: string;
  email_draft_subject: string | null;
  email_draft_body: string | null;
  email_status: string;
  send_scheduled_at: string | null;
  last_contacted_at: string | null;
}

export function useReferralLeads() {
  const { user } = useAuth();
  const [referralLeads, setReferralLeads] = useState<ReferralLead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchReferralLeads = useCallback(async () => {
    if (!user) { setReferralLeads([]); setLoading(false); return; }
    // The project's PostgREST max_rows is 1000 — a single request silently
    // truncates past that regardless of any .range() requested. Page through
    // in 1000-row batches so accounts with more referral leads than that
    // don't get silently wrong counts/stats everywhere this hook is used
    // (Reports, Analytics, etc.) — same fix as the Export Referrals cap.
    const PAGE_SIZE = 1000;
    const FETCH_CAP = 50_000;
    const all: any[] = [];
    for (let offset = 0; offset < FETCH_CAP; offset += PAGE_SIZE) {
      const { data: page, error } = await supabase
        .from("referral_leads")
        .select("*")
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) break;
      all.push(...(page ?? []));
      if (!page || page.length < PAGE_SIZE) break;
    }
    setReferralLeads(all);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchReferralLeads(); }, [fetchReferralLeads]);

  const addReferralLead = useCallback(async (lead: Omit<ReferralLead, "id" | "created_at" | "enriched" | "enrichment_data" | "email_draft_subject" | "email_draft_body" | "email_status" | "send_scheduled_at" | "last_contacted_at">) => {
    const { error } = await supabase.from("referral_leads").insert(lead as any);
    if (error) throw error;
    await fetchReferralLeads();
  }, [fetchReferralLeads]);

  const updateReferralLead = useCallback(async (id: string, updates: Partial<ReferralLead>) => {
    const { error } = await supabase.from("referral_leads").update(updates as any).eq("id", id);
    if (error) throw error;
    await fetchReferralLeads();
  }, [fetchReferralLeads]);

  const deleteReferralLead = useCallback(async (id: string) => {
    const { error } = await supabase.from("referral_leads").delete().eq("id", id);
    if (error) throw error;
    await fetchReferralLeads();
  }, [fetchReferralLeads]);

  return { referralLeads, loading, addReferralLead, updateReferralLead, deleteReferralLead, refetch: fetchReferralLeads };
}
